import React from "react";
import Chart from "../../common/ApexChart";
import Loader from "../../common/Loader";
import allApi from "../../../../api/allApi";
import { useEffect, useState } from "react";
import { useLocalization } from "../../localization/LocalizationContext";
import { useNetwork } from "../../common/networkContext";

function ConnectedClientExpandation() {
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId, selectedLocation } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001;
  const [ConnectedClientSeries, setConnectedClientSeries] = useState([]);
  const [ConnectedClientCategories, setConnectedClientCategories] = useState(
    []
  );
  const [loading, setLoading] = useState(true);
  const [connectedClientsDuration, setConnectedClientsDuration] =
    useState("24h");

  useEffect(() => {
    getConnectedClientPerSSID(connectedClientsDuration,networkId);
  }, [connectedClientsDuration,networkId]);

  const getConnectedClientPerSSID = async (connectedClientsDuration,networkId) => {
    try {
      setLoading(true);
      const api = new allApi();

      // Fetch data with the specified duration
      const response = await api.getRequest(
        `api/wireless/connectedClientsPerSsid?commandType=2&duration=${connectedClientsDuration}&networkId=${networkId}`
      );

      if (response.status === "OK") {
        const data = response.response;

        const seriesData = data.map((item) => ({
          name: item.ssidName,
          data: item.data.map((client) => {
            // Parse timestamp based on `interval1`
            const date = new Date(client.interval1);

            // Round time based on the duration
            if (
              connectedClientsDuration === "8h" ||
              connectedClientsDuration === "24h"
            ) {
              date.setMinutes(0, 0, 0); // Round to the start of the hour
            } else if (
              connectedClientsDuration === "7d" ||
              connectedClientsDuration === "30d"
            ) {
              date.setHours(0, 0, 0, 0); // Round to the start of the day
            }

            return {
              x: date.getTime(),
              y: client.totalClientSum,
            };
          }),
        }));

        // Extract unique rounded timestamps for the x-axis categories
        const timeStamps = [
          ...new Set(
            seriesData.flatMap((series) => series.data.map((point) => point.x))
          ),
        ];

        setConnectedClientCategories(timeStamps);
        setConnectedClientSeries(seriesData);
      } else {
        console.error("Failed to fetch data:", response.message);
      }
    } catch (error) {
      console.error("Error fetching connected client data:", error);
    } finally {
      setLoading(false);
    }
  };

  const connectedClientsOptions = {
    chart: {
      type: "line",
      height: 350,
      toolbar: { show: false },
    },
    xaxis: {
      type: "datetime",
      tickAmount: 6,

      categories: ConnectedClientCategories,
      labels: {
        formatter: (value) => {
          const date = new Date(value);

          // Format based on the duration selected
          if (
            connectedClientsDuration === "7d" ||
            connectedClientsDuration === "30d"
          ) {
            return date.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
            }); // DD MMM format
          } else {
            return date.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            });
          }
        },
        style: {
          colors: "#9aa0ac",
          fontSize: "12px",
        },
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      min: 0,
      title: { text: "Client Count" },
      labels: { formatter: (val) => val.toFixed(0) },
    },
    stroke: { width: 1 },
    dataLabels: { enabled: false },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      showForSingleSeries: true,
      labels: {
        colors: "#9aa0ac",
        fontSize: "12px",
      },
    },
    zoom: { enabled: false },
  };

  const handleConnectedClientsDurationChange = (newDuration) => {
    setConnectedClientsDuration(newDuration);
  };

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="inc-card ch-100">
          <div className="inc-card-title">
            <h3>{localizationStrings?.connectedClientsPerSSID}</h3>
            <div className="card-title-dropdown">
              <div className="btn-group">
                <span className="dropdown" data-bs-toggle="dropdown">
                  <span>
                    {connectedClientsDuration === "24h"
                      ? localizationStrings?.last24Hours
                      : connectedClientsDuration}
                    <i className="fa fa-angle-down" aria-hidden="true"></i>
                  </span>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                      <a
                        className="dropdown-item"
                        onClick={() =>
                          handleConnectedClientsDurationChange("8h")
                        }
                      >
                        {localizationStrings?.last8Hours}
                      </a>
                    </li>
                    <li>
                      <a
                        className="dropdown-item"
                        onClick={() =>
                          handleConnectedClientsDurationChange("24h")
                        }
                      >
                        {localizationStrings?.last24Hours}
                      </a>
                    </li>
                    <li>
                      <a
                        className="dropdown-item"
                        onClick={() =>
                          handleConnectedClientsDurationChange("7d")
                        }
                      >
                        {localizationStrings?.last7Days}
                      </a>
                    </li>
                    <li>
                      <a
                        className="dropdown-item"
                        onClick={() =>
                          handleConnectedClientsDurationChange("30d")
                        }
                      >
                        {localizationStrings?.last30Days}
                      </a>
                    </li>
                  </ul>
                </span>
              </div>
              <button
                className="icon-btn"
                title="Refresh"
                onClick={() =>
                  getConnectedClientPerSSID(connectedClientsDuration,networkId)
                }
              >
                <i className="fa fa-refresh" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          <div className="inc-card-body">
            <div className="row">
              <div className="col-lg-8">
                {loading ? (
                  <Loader />
                ) : (
                  <Chart
                    options={connectedClientsOptions}
                    series={ConnectedClientSeries}
                    type="line"
                    height={350}
                  />
                )}
              </div>
              <div className="col-lg-4">
                <div className="round-border">
                  <div className="inc-card-table">
                    <table className="m-head">
                      <thead>
                        <tr>
                          <th>{localizationStrings?.ssidName}</th>
                          <th>{localizationStrings?.clientCount}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ConnectedClientSeries.flatMap((seriesItem) =>
                          seriesItem.data.map((dataItem, index) => (
                            <tr key={index}>
                              <td>{seriesItem.name}</td>
                              <td>{dataItem.y}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConnectedClientExpandation;
